// Test data storage utility for managing test state across pages
// SECURITY NOTE: localStorage is ONLY for test progress, NEVER for final results

const TEST_STORAGE_KEY = 'bestiq_test_data'

// Initialize test data - ONLY for test-taking, not results viewing
export const initializeTest = (questions) => {
  const testData = {
    answers: {},
    score: 0,
    totalQuestions: questions ? questions.length : 50,
    iqScore: 0,
    percentile: 0,
    startTime: new Date().getTime(),
    endTime: null,
    currentQuestionIndex: 0,
    isCompleted: false
  }

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(TEST_STORAGE_KEY, JSON.stringify(testData))
    } catch (error) {
      console.error('Error initializing test data:', error)
    }
  }

  return testData
}

// Get test data from storage - ONLY for test progress, NOT results
export const getTestData = () => {
  if (typeof window === 'undefined') return null
  
  try {
    const data = localStorage.getItem(TEST_STORAGE_KEY)
    return data ? JSON.parse(data) : null
  } catch (error) {
    console.error('Error getting test data:', error)
    return null
  }
}

// Update test data in storage - ONLY for test progress
export const updateTestData = (updates) => {
  if (typeof window === 'undefined') return null
  
  try {
    const currentData = getTestData()
    if (!currentData) return null
    
    const updatedData = { ...currentData, ...updates }
    localStorage.setItem(TEST_STORAGE_KEY, JSON.stringify(updatedData))
    return updatedData
  } catch (error) {
    console.error('Error updating test data:', error)
    return null
  }
}

// Save answer for a question - ONLY during test-taking
export const saveAnswer = (questionId, answerIndex) => {
  const currentData = getTestData()
  if (!currentData) return null
  
  const updatedAnswers = {
    ...currentData.answers,
    [questionId]: answerIndex
  }
  
  return updateTestData({ 
    answers: updatedAnswers 
  })
}

// Calculate final results - ONLY for payment submission, NOT display
export const calculateResults = (finalAnswers, questions) => {
  let correctAnswers = 0
  
  questions.forEach(question => {
    const userAnswer = finalAnswers[question.id]
    if (userAnswer === question.correctAnswer) {
      correctAnswers++
    }
  })

  const percentageCorrect = (correctAnswers / questions.length) * 100
  const zScore = (percentageCorrect - 50) / 15
  let iqScore = Math.round(100 + (zScore * 15))
  
  iqScore = Math.max(40, Math.min(200, iqScore))
  const percentile = Math.round(normalCDF(zScore) * 100)

  return {
    score: correctAnswers,
    iqScore,
    percentile,
    endTime: new Date().getTime()
  }
}

// Complete the test - ONLY for preparing payment data
export const completeTest = (finalAnswers, questions) => {
  const results = calculateResults(finalAnswers, questions)
  
  return updateTestData({
    answers: finalAnswers,
    ...results,
    isCompleted: true
  })
}

// SECURITY: Clear test data - MUST be called after successful payment
export const clearTestData = () => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(TEST_STORAGE_KEY)
    } catch (error) {
      console.error('Error clearing test data:', error)
    }
  }
}

// Check if test is in progress - ONLY for test-taking flow
export const isTestInProgress = () => {
  const data = getTestData()
  return data && !data.isCompleted
}

// Check if test is completed - ONLY for payment flow, NOT results display
export const isTestCompleted = () => {
  const data = getTestData()
  return data && data.isCompleted
}

// SECURITY WARNING: These functions below are ONLY for internal calculations
// NEVER use localStorage data to display final results to users!

const normalCDF = (z) => {
  return 0.5 * (1 + erf(z / Math.sqrt(2)))
}

const erf = (x) => {
  const a1 =  0.254829592
  const a2 = -0.284496736
  const a3 =  1.421413741
  const a4 = -1.453152027
  const a5 =  1.061405429
  const p  =  0.3275911

  const sign = x < 0 ? -1 : 1
  x = Math.abs(x)

  const t = 1.0 / (1.0 + p * x)
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x)

  return sign * y
}